import { useState, useEffect } from "react";
import {
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Building2,
  ArrowRight,
  RefreshCw,
  Lock,
  Mail,
  User,
  LogIn,
  UserPlus,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { ACTIVE_CRM_STORAGE_KEY } from "../context/CRMContext";

export const ActivateCrmPage = () => {
  const [token, setToken] = useState<string>("");
  const [crmInfo, setCrmInfo] = useState<{ name: string; slug: string } | null>(
    null,
  );
  const [isValidating, setIsValidating] = useState(true);
  const [isInvalid, setIsInvalid] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Modo: "register" (novo usuário) ou "login" (já possui conta)
  const [authMode, setAuthMode] = useState<"register" | "login">("register");

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isActivated, setIsActivated] = useState(false);

  useEffect(() => {
    // Extrai o token da URL (suporta hash router ou search params)
    const hash = window.location.hash || "";
    let extractedToken = "";

    if (hash.includes("?")) {
      const searchParams = new URLSearchParams(hash.split("?")[1]);
      extractedToken = searchParams.get("token") || "";
    }
    if (!extractedToken && window.location.search) {
      const searchParams = new URLSearchParams(window.location.search);
      extractedToken = searchParams.get("token") || "";
    }

    setToken(extractedToken);

    if (!extractedToken) {
      setIsInvalid(true);
      setIsValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const client = getSupabaseClient();

        // 1. Verifica se usuário já está logado
        const { data: sessionData } = await client.auth.getSession();
        if (sessionData?.session?.user) {
          setCurrentUser(sessionData.session.user);
        }

        // 2. Consulta o CRM pelo token via RPC ou query direta
        const { data: rpcData, error: rpcErr } = await client.rpc(
          "get_crm_activation_info",
          { p_token: extractedToken },
        );

        if (!rpcErr && rpcData?.valid) {
          setCrmInfo({ name: rpcData.name, slug: rpcData.slug });
        } else {
          // Fallback query direta
          const { data: crmData } = await client
            .from("crms")
            .select("id, name, slug, status")
            .eq("activation_token", extractedToken)
            .eq("status", "pending_activation")
            .maybeSingle();

          if (crmData) {
            setCrmInfo({ name: crmData.name, slug: crmData.slug });
          } else {
            setIsInvalid(true);
          }
        }
      } catch (err) {
        console.error("Erro ao validar token de ativação:", err);
        setIsInvalid(true);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, []);

  const completeActivation = async (userId?: string) => {
    const client = getSupabaseClient();
    const { data, error } = await client.rpc("activate_crm_owner", {
      p_token: token,
    });

    if (error) {
      // Fallback update direto caso a RPC falhe
      const resolvedUserId =
        userId || (await client.auth.getSession()).data.session?.user?.id;
      if (!resolvedUserId) throw error;

      const { data: crmRow, error: updateErr } = await client
        .from("crms")
        .update({
          owner_id: resolvedUserId,
          status: "active",
          activation_token: null,
        })
        .eq("activation_token", token)
        .select()
        .single();

      if (updateErr) throw updateErr;

      if (crmRow) {
        localStorage.setItem(ACTIVE_CRM_STORAGE_KEY, crmRow.id);
        setIsActivated(true);
        setTimeout(() => {
          window.location.replace("/#/");
          window.location.reload();
        }, 1500);
        return;
      }
      throw error;
    }

    if (data?.crm_id) {
      localStorage.setItem(ACTIVE_CRM_STORAGE_KEY, data.crm_id);
    }
    setIsActivated(true);
    setTimeout(() => {
      window.location.replace("/#/");
      window.location.reload();
    }, 1500);
  };

  const handleActivateForCurrentUser = async () => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await completeActivation(currentUser?.id);
    } catch (err: any) {
      console.error("Erro ao ativar CRM:", err);
      setErrorMessage(err.message || "Falha ao ativar o CRM.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOutCurrentUser = async () => {
    try {
      const client = getSupabaseClient();
      await client.auth.signOut();
      setCurrentUser(null);
    } catch (err) {
      console.error("Erro ao sair:", err);
    }
  };

  const handleLoginAndActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const client = getSupabaseClient();
      const { data: signInData, error: signInErr } =
        await client.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

      if (signInErr) {
        throw new Error(
          signInErr.message.includes("Invalid login credentials")
            ? "E-mail ou senha incorretos."
            : signInErr.message,
        );
      }

      if (!signInData.user) {
        throw new Error("Não foi possível autenticar o usuário.");
      }

      // Agora autenticado, ativa a posse do CRM
      await completeActivation(signInData.user.id);
    } catch (err: any) {
      console.error("Erro no login e ativação:", err);
      setErrorMessage(err.message || "Erro ao entrar e ativar CRM.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterAndActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const client = getSupabaseClient();

      // 1. Tenta criar a conta no Supabase Auth
      const { data: signUpData, error: signUpErr } = await client.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
          },
        },
      });

      if (signUpErr) {
        const isAlreadyRegistered =
          signUpErr.message?.toLowerCase().includes("already registered") ||
          signUpErr.message?.toLowerCase().includes("already exists") ||
          (signUpErr as any).code === "user_already_exists";

        if (isAlreadyRegistered) {
          // O usuário já existe! Tentativa inteligente de login com a senha digitada:
          const { data: signInData, error: signInErr } =
            await client.auth.signInWithPassword({
              email: email.trim(),
              password,
            });

          if (!signInErr && signInData?.user) {
            // A senha digitada confere! Ativa o CRM diretamente sem atrito
            await completeActivation(signInData.user.id);
            return;
          }

          // Se a senha não conferiu, direciona para a aba de login com mensagem amigável
          setAuthMode("login");
          setErrorMessage(
            "Este e-mail já possui uma conta no sistema. Por favor, digite sua senha de acesso para entrar e assumir o CRM como Dono.",
          );
          return;
        }

        throw signUpErr;
      }

      // 2. Se a confirmação de e-mail não for obrigatória, tenta login imediato se necessário
      if (!signUpData.session) {
        const { data: signInData, error: signInErr } =
          await client.auth.signInWithPassword({
            email: email.trim(),
            password,
          });
        if (!signInErr && signInData?.user) {
          await completeActivation(signInData.user.id);
          return;
        }
      }

      // 3. Ativa a posse do CRM para este novo usuário
      await completeActivation(signUpData.user?.id);
    } catch (err: any) {
      console.error("Erro no cadastro e ativação do Dono:", err);
      let msg = err.message || "Erro ao criar conta e ativar CRM.";
      if (msg.includes("Password should be at least")) {
        msg = "A senha deve conter no mínimo 6 caracteres.";
      }
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm font-medium">Validando token de ativação...</p>
        </div>
      </div>
    );
  }

  if (isInvalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full border-border/80 shadow-lg text-center p-6">
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7" />
          </div>
          <CardTitle className="text-xl font-bold mb-2">
            Link de Ativação Inválido
          </CardTitle>
          <CardDescription className="text-xs text-muted-foreground mb-6 leading-relaxed">
            Este link de ativação é inválido, expirou ou o CRM já foi ativado
            anteriormente. Caso precise de acesso, entre em contato com o
            administrador da plataforma.
          </CardDescription>
          <Button
            variant="default"
            onClick={() => window.location.replace("/#/login")}
            className="w-full text-xs"
          >
            Ir para a Tela de Login
          </Button>
        </Card>
      </div>
    );
  }

  if (isActivated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4 animate-in fade-in-50 duration-300">
        <Card className="max-w-md w-full border-emerald-500/30 shadow-lg text-center p-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <CardTitle className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mb-2">
            CRM Ativado com Sucesso!
          </CardTitle>
          <CardDescription className="text-xs text-muted-foreground mb-6">
            Você agora é o Dono oficial do CRM <strong>{crmInfo?.name}</strong>.
            Redirecionando para o seu novo painel...
          </CardDescription>
          <div className="flex justify-center">
            <RefreshCw className="w-5 h-5 animate-spin text-emerald-600" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 sm:p-6 animate-in fade-in-50 duration-300">
      <Card className="max-w-lg w-full border-border/80 shadow-xl overflow-hidden">
        {/* Banner Superior */}
        <div className="bg-primary/10 border-b border-primary/20 p-6 text-center">
          <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 shadow-sm">
            <Building2 className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">
            {crmInfo?.name}
          </h1>
          <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1.5 font-medium">
            <ShieldCheck className="w-4 h-4 text-primary" />
            Ativação de Dono (Owner)
          </p>
        </div>

        <CardContent className="p-6 space-y-4">
          <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 text-xs text-muted-foreground leading-relaxed">
            Como <strong>Dono deste CRM</strong>, você terá controle absoluto
            deste ambiente, podendo gerenciar seus funis, convidar sua equipe de
            vendas e acompanhar todos os negócios.
          </div>

          {errorMessage && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Se o usuário já estiver logado */}
          {currentUser ? (
            <div className="space-y-4 pt-2">
              <div className="p-3.5 rounded-lg bg-primary/5 border border-primary/20 text-xs flex items-center justify-between">
                <div>
                  <span className="text-muted-foreground block text-[11px]">
                    Conectado como
                  </span>
                  <strong className="text-foreground text-sm">
                    {currentUser.email}
                  </strong>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOutCurrentUser}
                  className="text-xs text-muted-foreground hover:text-foreground h-8 gap-1"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Trocar conta
                </Button>
              </div>

              <Button
                onClick={handleActivateForCurrentUser}
                disabled={isSubmitting}
                className="w-full text-xs font-semibold gap-2 h-10 shadow-sm"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Ativando seu CRM...
                  </>
                ) : (
                  <>
                    Confirmar e Ativar como Dono
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Seletor de Modo: Criar Conta vs Já Tenho Conta */}
              <div className="grid grid-cols-2 p-1 bg-muted/50 rounded-lg border text-xs font-medium">
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode("register");
                    setErrorMessage("");
                  }}
                  className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-all ${
                    authMode === "register"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Criar Nova Conta
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode("login");
                    setErrorMessage("");
                  }}
                  className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-all ${
                    authMode === "login"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Já Tenho Conta
                </button>
              </div>

              {authMode === "register" ? (
                /* Formulário de Cadastro para novo Dono */
                <form
                  onSubmit={handleRegisterAndActivate}
                  className="space-y-3.5"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="firstName"
                        className="text-xs font-medium"
                      >
                        Nome *
                      </Label>
                      <div className="relative">
                        <User className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                        <Input
                          id="firstName"
                          placeholder="Ex: Carlos"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          className="pl-8 h-9 text-xs"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="lastName" className="text-xs font-medium">
                        Sobrenome *
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="Ex: Silva"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-medium">
                      Seu E-mail Profissional *
                    </Label>
                    <div className="relative">
                      <Mail className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu.email@empresa.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="pl-8 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs font-medium">
                      Crie sua Senha de Acesso *
                    </Label>
                    <div className="relative">
                      <Lock className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="Mínimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="pl-8 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-xs font-semibold gap-2 h-10 shadow-sm mt-2"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Criando conta e ativando...
                      </>
                    ) : (
                      <>
                        Criar Conta e Ativar CRM
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                /* Formulário de Login para usuário já existente */
                <form onSubmit={handleLoginAndActivate} className="space-y-3.5">
                  <div className="space-y-1.5">
                    <Label htmlFor="loginEmail" className="text-xs font-medium">
                      Seu E-mail *
                    </Label>
                    <div className="relative">
                      <Mail className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                      <Input
                        id="loginEmail"
                        type="email"
                        placeholder="seu.email@empresa.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="pl-8 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="loginPassword"
                      className="text-xs font-medium"
                    >
                      Sua Senha de Acesso *
                    </Label>
                    <div className="relative">
                      <Lock className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                      <Input
                        id="loginPassword"
                        type="password"
                        placeholder="Digite sua senha"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pl-8 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-xs font-semibold gap-2 h-10 shadow-sm mt-2"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Entrando e ativando...
                      </>
                    ) : (
                      <>
                        Entrar e Ativar CRM
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
